import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import httpClient from '../../helpers/httpClient'
import { NumericFormat } from 'react-number-format'
import { toast } from 'react-toastify'
import PopupInput from '../../PopupInput'
import { showNotificationPopup } from '../../NotificationPopup'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { invoiceApi, invoiceError } from '../../invoicing/api'
import './PriceSection.css'

const defaultPriceState = {
  land_price: 0,
  land_tax_mode: 'registration',
  land_vat_percentage: 0.21,
  land_registration_percentage: 0.12,
  construction_price: 0,
  construction_tax_mode: 'vat',
  construction_vat_percentage: 0.21,
  construction_registration_percentage: 0.12,
  connection_cost: 0,
  contract_cost: 0,
  vat_percentage: 0.21,
  connection_vat_percentage: 0.21,
  contract_vat_percentage: 0.21,
  registration_percentage: 0.12,
}

const toNumber = (value, fallback = 0) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

const PriceSection = forwardRef(({ unit }, ref) => {
  const [commercialPrice, setCommercialPrice] = useState(defaultPriceState)
  const [transactionPrice, setTransactionPrice] = useState(defaultPriceState)
  const [additionalWorkCosts, setAdditionalWorkCosts] = useState({ cost: 0, discount_value: 0, actual: 0 })
  const [paymentSchedule, setPaymentSchedule] = useState([])
  const [manualInvoiceNotes, setManualInvoiceNotes] = useState({})
  const scheduleLocked = paymentSchedule.some(stage => stage.locked)

  const normalizePriceData = (raw = {}) => {
    const fallbackVat = toNumber(raw.vat_percentage, 0.21)
    const fallbackReg = toNumber(raw.registration_percentage, 0.12)
    return {
      ...defaultPriceState,
      ...raw,
      land_tax_mode: raw.land_tax_mode || 'registration',
      land_vat_percentage: toNumber(raw.land_vat_percentage, fallbackVat),
      land_registration_percentage: toNumber(raw.land_registration_percentage, fallbackReg),
      construction_tax_mode: raw.construction_tax_mode || 'vat',
      construction_vat_percentage: toNumber(raw.construction_vat_percentage, fallbackVat),
      construction_registration_percentage: toNumber(raw.construction_registration_percentage, fallbackReg),
      connection_vat_percentage: toNumber(raw.connection_vat_percentage, fallbackVat),
      contract_vat_percentage: toNumber(raw.contract_vat_percentage, fallbackVat),
      vat_percentage: fallbackVat,
      registration_percentage: fallbackReg,
    }
  }

  const calculateTotals = (prices) => {
    const land = toNumber(prices.land_price)
    const construction = toNumber(prices.construction_price)
    const utility = toNumber(prices.connection_cost)
    const contract = toNumber(prices.contract_cost)

    const landVat = prices.land_tax_mode === 'vat' ? land * toNumber(prices.land_vat_percentage) : 0
    const landReg = prices.land_tax_mode === 'registration' ? land * toNumber(prices.land_registration_percentage) : 0

    const constructionVat = prices.construction_tax_mode === 'vat' ? construction * toNumber(prices.construction_vat_percentage) : 0
    const constructionReg = prices.construction_tax_mode === 'registration' ? construction * toNumber(prices.construction_registration_percentage) : 0

    const utilityVat = utility * toNumber(prices.connection_vat_percentage)
    const contractVat = contract * toNumber(prices.contract_vat_percentage)

    const subtotal = land + construction + utility + contract
    const vatTotal = landVat + constructionVat + utilityVat + contractVat
    const registrationTotal = landReg + constructionReg

    return {
      subtotal,
      vatTotal,
      registrationTotal,
      total: subtotal + vatTotal + registrationTotal,
    }
  }

  useImperativeHandle(ref, () => ({
    handleSavePriceDetails: async () => {
      const stageNumbers = paymentSchedule.map(stage => stage.stage_number)
      if (stageNumbers.some(num => num === undefined || num === null || num === '')) {
        throw new Error('Invalid payment schedule: Empty stage numbers')
      }
      if (new Set(stageNumbers).size !== stageNumbers.length) {
        throw new Error('Invalid payment schedule: Duplicate stage numbers')
      }
      const sorted = [...stageNumbers].sort((a, b) => a - b)
      for (let i = 0; i < sorted.length - 1; i++) {
        if (sorted[i + 1] !== sorted[i] + 1) {
          throw new Error('Invalid payment schedule: Non-sequential stage numbers')
        }
      }

      try {
        await invoiceApi(`/unit-price-details/${unit.id}`, {
          commercial_price: commercialPrice,
          transactional_price: transactionPrice,
          additional_work_costs: additionalWorkCosts,
          payment_schedule: paymentSchedule,
        })
      } catch (error) {
        throw new Error(invoiceError(error))
      }
    },
  }))

  useEffect(() => {
    const fetchPriceDetails = async () => {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/unit-price-details/${unit.id}`)
      if (response.status === 200) {
        setCommercialPrice(normalizePriceData(response.data.commercial_price || {}))
        setTransactionPrice(normalizePriceData(response.data.transactional_price || {}))
        const work = response.data.additional_work_costs || {}
        const cost = toNumber(work.cost)
        const discount = toNumber(work.discount_value)
        setAdditionalWorkCosts({ cost, discount_value: discount, actual: cost - discount })
        setPaymentSchedule(response.data.payment_schedule || [])
      }
    }
    fetchPriceDetails().catch(error => toast.error(invoiceError(error)))
  }, [unit.id])

  const updatePrice = (setter, field, value) => {
    setter(prev => ({ ...prev, [field]: value }))
  }

  const reloadStages = async () => {
    const data = await invoiceApi(`/unit-price-details/${unit.id}`, null, 'get')
    setPaymentSchedule(data.payment_schedule || [])
  }
  const markInvoiced = async (stage, note) => {
    if (!note?.trim()) { toast.error('Enter invoice reference or reason'); return }
    try {
      await invoiceApi(`/unit-payment-stages/${stage.id}/manual-invoice`, { note })
      await reloadStages()
      setManualInvoiceNotes(previous => ({ ...previous, [stage.id]: '' }))
      toast.success('Stage marked invoiced')
    } catch (e) { toast.error(invoiceError(e)) }
  }
  const resetToBuilding = async () => {
    showNotificationPopup({
      message: 'Replace this unit’s stages with current building instalments? Unsaved stage changes will be lost.',
      actionButton: { text: 'Reset', color: 'var(--primary-color)' },
      onAction: async () => {
        try {
          await invoiceApi(`/units/${unit.id}/payment-schedule/reset`, {})
          await reloadStages()
          toast.success('Instalments reset to building defaults')
        } catch (e) { toast.error(invoiceError(e)) }
      }
    })
  }
  const renderVatInput = (value, onChange, disabled = false) => (
    <select aria-label="VAT rate" className="input_field__input_box--text" value={value} disabled={disabled} onChange={e => onChange(Number(e.target.value))}>
      {[0, 0.06, 0.12, 0.21].map(rate => <option key={rate} value={rate}>{rate * 100}%</option>)}
      {![0, 0.06, 0.12, 0.21].includes(Number(value)) && <option value={value}>Invalid stored VAT ({Number(value) * 100}%)</option>}
    </select>
  )

  const renderMoneyInput = (value, onChange, disabled = false) => (
    <div className="input-with-euro">
      <span className="euro-sign">€</span>
      <NumericFormat
        className="input_field__input_box--text"
        value={value}
        thousandSeparator=" "
        decimalSeparator="," 
        decimalScale={2}
        allowNegative={false}
        disabled={disabled}
        onValueChange={({ floatValue }) => !disabled && onChange(floatValue || 0)}
      />
    </div>
  )

  const renderPercentInput = (value, onChange, disabled = false) => (
    <div className="input-with-euro percent">
      <NumericFormat
        className="input_field__input_box--text"
        value={(toNumber(value) * 100)}
        thousandSeparator=" "
        decimalSeparator="," 
        decimalScale={2}
        allowNegative={false}
        disabled={disabled}
        onValueChange={({ floatValue }) => !disabled && onChange(floatValue !== undefined ? floatValue / 100 : 0)}
      />
      <span className="euro-sign">%</span>
    </div>
  )

  // Render helper, not an inline component type: preserve input DOM and keyboard focus.
  const renderPriceCard = ({ title, data, setData, readOnly = false }) => {
    const totals = calculateTotals(data)

    return (
      <div className="price-card">
        <div className="price-card__title-row">
          <h3>{title}</h3>
          {readOnly && <span className="price-card__badge">auto</span>}
        </div>

        <div className="price-card__table">
          <div className="price-card__head">Item</div>
          <div className="price-card__head">Amount</div>
          <div className="price-card__head">Tax Mode</div>
          <div className="price-card__head">Tax %</div>

          <div className="price-card__cell price-card__item">Land</div>
          <div className="price-card__cell">{renderMoneyInput(data.land_price, (v) => updatePrice(setData, 'land_price', v), readOnly)}</div>
          <div className="price-card__cell">
            <select
              className="input_field__input_box--text"
              value={data.land_tax_mode}
              disabled={readOnly}
              onChange={(e) => updatePrice(setData, 'land_tax_mode', e.target.value)}
            >
              <option value="registration">Registration</option>
              <option value="vat">VAT</option>
            </select>
          </div>
          <div className="price-card__cell">
            {data.land_tax_mode === 'vat'
              ? renderVatInput(data.land_vat_percentage, (v) => updatePrice(setData, 'land_vat_percentage', v), readOnly)
              : renderPercentInput(data.land_registration_percentage, (v) => updatePrice(setData, 'land_registration_percentage', v), readOnly)}
          </div>

          <div className="price-card__cell price-card__item">Construction</div>
          <div className="price-card__cell">{renderMoneyInput(data.construction_price, (v) => updatePrice(setData, 'construction_price', v), readOnly)}</div>
          <div className="price-card__cell">
            <select
              className="input_field__input_box--text"
              value={data.construction_tax_mode}
              disabled={readOnly}
              onChange={(e) => updatePrice(setData, 'construction_tax_mode', e.target.value)}
            >
              <option value="vat">VAT</option>
              <option value="registration">Registration</option>
            </select>
          </div>
          <div className="price-card__cell">
            {data.construction_tax_mode === 'vat'
              ? renderVatInput(data.construction_vat_percentage, (v) => updatePrice(setData, 'construction_vat_percentage', v), readOnly)
              : renderPercentInput(data.construction_registration_percentage, (v) => updatePrice(setData, 'construction_registration_percentage', v), readOnly)}
          </div>

          <div className="price-card__cell price-card__item">Utility</div>
          <div className="price-card__cell">{renderMoneyInput(data.connection_cost, (v) => updatePrice(setData, 'connection_cost', v), readOnly)}</div>
          <div className="price-card__cell muted">VAT only</div>
          <div className="price-card__cell">{renderVatInput(data.connection_vat_percentage, (v) => updatePrice(setData, 'connection_vat_percentage', v), readOnly)}</div>

          <div className="price-card__cell price-card__item">Contract</div>
          <div className="price-card__cell">{renderMoneyInput(data.contract_cost, (v) => updatePrice(setData, 'contract_cost', v), readOnly)}</div>
          <div className="price-card__cell muted">VAT only</div>
          <div className="price-card__cell">{renderVatInput(data.contract_vat_percentage, (v) => updatePrice(setData, 'contract_vat_percentage', v), readOnly)}</div>
        </div>

        <div className="price-card__summary">
          <div>Subtotal: €{totals.subtotal.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div>VAT: €{totals.vatTotal.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div>Registration: €{totals.registrationTotal.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className="price-card__summary-total">Total: €{totals.total.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="section-container" id="unit-price-section">
      <h2>Price Details</h2>
      <div className="price-section">
        {renderPriceCard({ title: 'Commercial price', data: commercialPrice, setData: setCommercialPrice })}
      </div>
      <div className="price-section">
        {renderPriceCard({ title: 'Transactional price', data: transactionPrice, setData: setTransactionPrice, readOnly: true })}
      </div>

      <div className="price-section">
        <h3>Additional Work</h3>
        <div className="price-grid horizontal">
          <div className="price-inputs">
            <div className="input_field--combo-grid">
              <label className="input_field__label">Cost</label>
              {renderMoneyInput(additionalWorkCosts.cost, () => {}, true)}
            </div>
            <div className="input_field--combo-grid">
              <label className="input_field__label">Discount</label>
              {renderMoneyInput(additionalWorkCosts.discount_value, (v) => setAdditionalWorkCosts(prev => ({ ...prev, discount_value: v, actual: (prev.cost || 0) - v })))}
            </div>
            <div className="input_field--combo-grid">
              <label className="input_field__label">Actual</label>
              {renderMoneyInput(additionalWorkCosts.actual, () => {}, true)}
            </div>
          </div>
        </div>
      </div>

      <div className="price-section">
        <h3>Instalments</h3>
        <div className="unit-stage-card">
          <div className="unit-stage-scroll">
            <div className="unit-stage-columns"><span>#</span><span>Description</span><span>%</span><span>Ready</span><span>Status</span><span></span></div>
          {paymentSchedule.map((stage, index) => (
            <div key={stage.id || index} className="unit-stage-row">
              <span className="unit-stage-number">{index + 1}</span>
              <input aria-label="Stage description" disabled={scheduleLocked} className="input_field__input_box--text" type="text" value={stage.stage_name} onChange={(e) => {
                setPaymentSchedule(previous => previous.map((item, itemIndex) =>
                  itemIndex === index ? { ...item, stage_name: e.target.value } : item))
              }} />
              <input aria-label="Stage percentage" disabled={scheduleLocked} min="0" max="100" step="0.000001" className="input_field__input_box--text" type="number" value={stage.payment_percentage} onChange={(e) => {
                setPaymentSchedule(previous => previous.map((item, itemIndex) =>
                  itemIndex === index ? { ...item, payment_percentage: e.target.value } : item))
              }} />
              <label className="unit-stage-ready"><input type="checkbox" aria-label={`${stage.stage_name || `Stage ${index + 1}`} ready`} checked={!!stage.ready} disabled={scheduleLocked}
                onChange={event => setPaymentSchedule(previous => previous.map((item, itemIndex) =>
                  itemIndex === index ? { ...item, ready: event.target.checked } : item))} /></label>
              <span className="unit-stage-status">{stage.billing_status?.replaceAll('_', ' ') || 'not invoiced'}</span>
              <div className="unit-stage-actions">{stage.id && !stage.locked && <PopupInput
                title="Mark stage invoiced"
                fields={[{ id: 'note', label: 'External invoice reference or reason', type: 'text' }]}
                values={{ note: manualInvoiceNotes[stage.id] || '' }}
                onChange={values => setManualInvoiceNotes(previous => ({ ...previous, [stage.id]: values.note }))}
                onSave={values => markInvoiced(stage, values.note)}
                customButton={<button type="button" className="unit-stage-icon-button" title="Mark already invoiced" aria-label={`Mark ${stage.stage_name} already invoiced`}><ReceiptLongIcon /></button>}
              />}
              {!scheduleLocked && <button type="button" title="Remove stage" aria-label={`Remove ${stage.stage_name || `stage ${index + 1}`}`} className="unit-stage-icon-button remove" onClick={() => setPaymentSchedule(prev => prev.filter((_, i) => i !== index).map((item, i) => ({ ...item, stage_number: i + 1 })))}><DeleteOutlineIcon /></button>}</div>
            </div>
          ))}
          </div>
          <div className="unit-stage-footer">
            {!scheduleLocked && <button type="button" className="unit-stage-action-button" onClick={() =>
              setPaymentSchedule(prev => [...prev, { stage_number: prev.length + 1, stage_name: '', payment_percentage: 0, ready: false }])}><AddIcon /> Add stage</button>}
            <button type="button" className="unit-stage-action-button" disabled={scheduleLocked} onClick={resetToBuilding}><RestartAltIcon /> Reset to building</button>
            <span className={`unit-stage-total ${Math.abs(paymentSchedule.reduce((sum, stage) => sum + (Number(stage.payment_percentage) || 0), 0) - 100) > 0.000001 ? 'invalid-total' : ''}`}>
              Total: {paymentSchedule.reduce((sum, stage) => sum + (Number(stage.payment_percentage) || 0), 0).toFixed(2)}%</span>
          </div>
          {scheduleLocked && <p className="unit-stage-lock-note">Reserved or invoiced stages lock this unit schedule.</p>}
        </div>
      </div>
    </div>
  )
})

PriceSection.displayName = 'PriceSection'
export default PriceSection
